import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import PostMioLogo from '../components/PostMioLogo'
import { usePWAInstall } from '../context/PWAInstallContext'

// ── Palette ────────────────────────────────────────────────
const G = {
  bg:      '#0A0A0A',
  base:    '#111111',
  card:    '#1A1A1A',
  hover:   '#222222',
  border:  '#2C2C2C',
  borderHi:'#3A3A3A',
  gold:    '#C9922A',
  goldLt:  '#E5B050',
  goldGlow:'rgba(201,146,42,0.12)',
  red:     '#A02020',
  redLt:   '#C42828',
  smoke:   '#7A7A7A',
  cream:   '#F0EDE8',
  textSec: '#A0A0A0',
  textMut: '#606060',
}

// ── i18n ───────────────────────────────────────────────────
const UI_TEXT = {
  IT: {
    nav: { login: 'Accedi', signup: 'Registrati', about: 'Chi siamo', reviews: 'Recensioni' },
    hero: {
      tag: 'Fatto per i piccoli imprenditori – da uno di loro',
      h1a: 'I tuoi post social',
      h1b: 'pronti in 10 secondi.',
      sub: 'Scrivi il piatto del giorno o incolla una recensione Google. PostMio™ genera 3 post perfetti per Instagram, Facebook e WhatsApp. Copia e pubblica. Fine.',
      cta: 'Inizia gratis per 7 giorni →',
      fine: 'Nessun addebito per 7 giorni \u00a0·\u00a0 Disdici quando vuoi \u00a0·\u00a0 €9,99/mese dopo',
      magicCta: 'Prova la magia di PostMio™ — nessuna carta richiesta',
    },
    problem: {
      h2a: 'Sai già che dovresti postare di più.',
      h2b: 'Ma non hai mai il tempo.',
      sub: "La cucina è piena, il telefono squilla, i tavoli sono occupati. I social media sono l'ultima cosa a cui pensi — ma i tuoi concorrenti postano ogni giorno. PostMio™ risolve questo problema in 10 secondi.",
    },
    how: {
      tag: 'Come funziona',
      h2: 'Tre passi. Dieci secondi.',
      steps: [
        { num: '1', title: 'Cosa succede oggi da te?', desc: 'Scrivi un aggiornamento, un\u2019offerta, un evento — qualsiasi cosa stia succedendo nel tuo locale oggi.' },
        { num: '2', title: 'Scegli il tono', desc: 'Caldo e familiare, professionale, o vivace. Tu scegli, PostMio™ scrive.' },
        { num: '3', title: 'Copia e pubblica', desc: '3 post pronti per Instagram, Facebook e WhatsApp. Un click per copiare.' },
      ],
    },
    features: {
      tag: 'Funzionalità',
      h2: 'Tutto quello che ti serve. Niente di più.',
      list: [
        { icon: '📱', title: 'Instagram, Facebook, WhatsApp', desc: '3 post ottimizzati per ogni piattaforma, generati insieme in un colpo solo.' },
        { icon: '⭐', title: 'Risposte alle recensioni', desc: 'Recensione negativa? PostMio™ ti aiuta a rispondere con professionalità in secondi.' },
        { icon: '🎭', title: '3 toni diversi', desc: 'Caldo, professionale o vivace. Il tono giusto per ogni momento.' },
        { icon: '⚡', title: 'Pronto in 10 secondi', desc: 'Zero attese. Scrivi, genera, copia. Torna ai tuoi clienti.' },
      ],
    },
    testimonials: {
      tag: 'Recensioni',
      h2: 'Cosa dicono i nostri utenti',
      list: [
        { text: '"Non avevo mai tempo per i social. Con PostMio™ ci metto 30 secondi al giorno. I clienti commentano che siamo sempre attivi."', name: 'Marco R.', role: 'Titolare, Trattoria da Marco — Genova' },
        { text: '"Ho risposto a una recensione negativa in modo professionale per la prima volta in vita mia. Il cliente è tornato e si è scusato."', name: 'Giulia T.', role: 'Proprietaria, Bar Centrale — La Spezia' },
        { text: '"Costa meno di un caffè al giorno e mi fa risparmiare ore ogni settimana. Non capisco perché non l\'ho trovato prima."', name: 'Antonio M.', role: 'Pizzaiolo, Pizzeria Bella Napoli — Napoli' },
      ],
    },
    pricing: {
      tag: 'Prezzo',
      h2: 'Un prezzo. Nessuna sorpresa.',
      sub: 'Meno di un caffè al giorno per non pensarci più.',
      period: '/mese',
      trial: '7 giorni gratis · Poi €9,99/mese · Disdici quando vuoi',
      features: [
        'Post illimitati per Instagram, Facebook, WhatsApp',
        'Risposte alle recensioni Google',
        '3 toni: caldo, professionale, vivace',
        'Copia in un click',
        'Accesso immediato',
      ],
      cta: 'Inizia gratis per 7 giorni →',
    },
    foundingCta: {
      tag: 'Founding Members',
      spotsLine: '50 posti disponibili · Scade il 15 aprile 2026',
      h2: 'Entra ora. Prima che sia tardi.',
      sub: 'I primi 50 abbonati pagano €7,99/mese per 6 mesi. Poi €9,99/mese. Usa il codice FOUNDING2026 al pagamento.',
      codeLabel: 'Il tuo codice:',
      cta: 'Diventa Founding Member →',
      finePrint: 'Dopo 6 mesi passa automaticamente a €9,99/mese · Disdici quando vuoi',
    },
    mlats: {
      tag: 'M-LATS™',
      h2: 'Scrivi nella tua lingua. Pubblica nella tua.',
      body: 'Scrivi il tuo aggiornamento in qualsiasi lingua tu voglia — PostMio™ genera i post nella lingua salvata nel tuo profilo. Specifico per categoria. Progettato per i titolari di attività stranieri che gestiscono la comunicazione in italiano ogni giorno.',
      callout: 'Usato da titolari di attività immigrate in 🇮🇹 🇵🇱 🇩🇪 🇬🇧 🇪🇸 🇫🇷',
      inputLabel: 'Input',
      outputLabel: 'Output',
    },
    review: {
      tag: 'Recensioni Google',
      h2: 'Trasforma le recensioni negative in opportunità.',
      body: 'Incolla qualsiasi recensione Google e ricevi una risposta professionale in pochi secondi. Funziona anche per gli attacchi a 1 stella. Proteggi la tua reputazione senza stress.',
      bullets: ['Risponde nella tua lingua', 'Calma, professionale, mai aggressiva', 'Pronta in meno di 10 secondi'],
      negativeText: 'Cibo arrivato freddo e servizio lentissimo. Non tornerò mai più.',
      responseText: 'Gentile Giovanni, la ringraziamo per il suo feedback. Siamo dispiaciuti per l\'esperienza vissuta e ci impegniamo a migliorare. La invitiamo a darci una seconda possibilità — saremo felici di rimediare.',
      timestamp: '2 giorni fa',
      generatedIn: '✓ Risposta professionale generata in 8 secondi',
    },
    faq: {
      tag: 'FAQ',
      h2: 'Domande frequenti',
      items: [
        { q: 'Serve una carta di credito per la prova gratuita?', a: 'Sì, è richiesta. Se disdici entro 7 giorni, non viene addebitato nulla. Nessuna sorpresa.' },
        { q: 'Cosa succede dopo la prova?', a: 'Dopo 7 giorni l\'abbonamento passa automaticamente a €9,99/mese. Puoi disdire in qualsiasi momento dalla dashboard, senza penali.' },
        { q: 'In cosa si differenzia PostMio™ da ChatGPT?', a: 'PostMio™ è costruito appositamente per i post social delle piccole imprese. Nessun prompt, nessuna configurazione. Apri, scrivi, copia. Fine.' },
        { q: 'Funziona per il mio tipo di attività?', a: 'Sì. PostMio™ supporta 6 categorie: Gastronomia, Bellezza & Benessere, Graphic Design & Creatività, Coaching & Consulenza, Ospitalità & Turismo, Retail & Shop.' },
        { q: 'Cos\'è M-LATS™?', a: 'M-LATS™ ti permette di scrivere in qualsiasi lingua e ricevere i post nella lingua salvata nel tuo profilo. Una funzionalità unica di PostMio™.' },
        { q: 'Posso disdire in qualsiasi momento?', a: 'Sì. Nessun contratto, nessuna domanda. Disdici dalla dashboard con un solo click.' },
      ],
    },
    compare: {
      tag: 'Confronto prezzi',
      h2: 'Perché PostMio™?',
      sub: "Confronta il costo reale. Non c'è gara.",
      smm: 'Social Media Manager',
      features: ['Post illimitati', 'Instagram + Facebook + WhatsApp', 'Risposte alle recensioni', 'AI specifico per categoria', 'M-LATS (output in tua lingua)', 'Senza limiti o token'],
      note: '* Prezzi indicativi. SMM freelance: €200–500/mese stimato. Predis.ai e SocialBee: prezzi a partire da €29/mese.',
    },
    footer: {
      tagline: 'Fatto con ❤️ in Italia · Da un piccolo imprenditore - per i piccoli imprenditori',
      about: 'Chi siamo',
      reviews: 'Recensioni',
      login: 'Accedi',
      signup: 'Registrati',
      legal: `© ${new Date().getFullYear()} PostMio™ · P.IVA in corso di registrazione`,
    },
  },

  EN: {
    nav: { login: 'Log in', signup: 'Sign up', about: 'About', reviews: 'Reviews' },
    hero: {
      tag: 'Built for small business owners – by one of them',
      h1a: 'Your social posts,',
      h1b: 'ready in 10 seconds.',
      sub: 'Describe the dish of the day or paste a Google review. PostMio™ generates 3 perfect posts for Instagram, Facebook, and WhatsApp. Copy and publish. Done.',
      cta: 'Start free for 7 days →',
      fine: 'No charge for 7 days \u00a0·\u00a0 Cancel anytime \u00a0·\u00a0 €9.99/month after',
      magicCta: 'Try the PostMio™ magic — no card required',
    },
    problem: {
      h2a: 'You know you should post more.',
      h2b: 'But you never have the time.',
      sub: "The kitchen is full, the phone is ringing, tables are busy. Social media is the last thing on your mind — yet your competitors post every day. PostMio™ solves that in 10 seconds.",
    },
    how: {
      tag: 'How it works',
      h2: 'Three steps. Ten seconds.',
      steps: [
        { num: '1', title: "What's happening today?", desc: 'Write an update, an offer, an event — anything happening at your place today.' },
        { num: '2', title: 'Pick a tone', desc: 'Warm and friendly, professional, or fun. You choose, PostMio™ writes.' },
        { num: '3', title: 'Copy and publish', desc: '3 posts ready for Instagram, Facebook, and WhatsApp. One click to copy.' },
      ],
    },
    features: {
      tag: 'Features',
      h2: "Everything you need. Nothing you don't.",
      list: [
        { icon: '📱', title: 'Instagram, Facebook, WhatsApp', desc: '3 platform-optimised posts generated together in one go.' },
        { icon: '⭐', title: 'Review replies', desc: 'Negative review? PostMio™ helps you respond professionally in seconds.' },
        { icon: '🎭', title: '3 different tones', desc: 'Warm, professional, or fun. The right tone for every occasion.' },
        { icon: '⚡', title: 'Ready in 10 seconds', desc: 'Zero waiting. Write, generate, copy. Get back to your customers.' },
      ],
    },
    testimonials: {
      tag: 'Reviews',
      h2: 'What our users say',
      list: [
        { text: '"I never had time for social media. With PostMio™ it takes me 30 seconds a day. Customers keep saying how active we are."', name: 'Marco R.', role: 'Owner, Trattoria da Marco — Genova' },
        { text: '"For the first time ever I replied to a negative review in a professional way. The customer came back and apologised."', name: 'Giulia T.', role: 'Owner, Bar Centrale — La Spezia' },
        { text: "\"It costs less than a coffee a day and saves me hours every week. I can't believe I didn't find it sooner.\"", name: 'Antonio M.', role: 'Pizzaiolo, Pizzeria Bella Napoli — Napoli' },
      ],
    },
    pricing: {
      tag: 'Pricing',
      h2: 'One price. No surprises.',
      sub: 'Less than a coffee a day, with nothing more to think about.',
      period: '/month',
      trial: '7 days free · Then €9.99/month · Cancel anytime',
      features: [
        'Unlimited posts for Instagram, Facebook, WhatsApp',
        'Google review replies',
        '3 tones: warm, professional, fun',
        'Copy with one click',
        'Instant access',
      ],
      cta: 'Start free for 7 days →',
    },
    foundingCta: {
      tag: 'Founding Members',
      spotsLine: '50 spots available · Expires 15 April 2026',
      h2: "Get in now. Before it's gone.",
      sub: 'The first 50 subscribers pay €7.99/month for 6 months and get 14 days for free as an Extra. Then €9.99/month. Don\'t miss out. Use code FOUNDING2026 at checkout.',
      codeLabel: 'Your code:',
      cta: 'Become a Founding Member →',
      finePrint: 'After 6 months auto-switches to €9,99/month · Cancel anytime',
    },
    mlats: {
      tag: 'M-LATS™',
      h2: 'Write in your language. Publish in yours.',
      body: 'Write your update in any language you want — PostMio™ generates posts in the language saved in your profile. Category-aware. Built for immigrant business owners who manage their business communication in a second language every day.',
      callout: 'Used by immigrant business owners across 🇮🇹 🇵🇱 🇩🇪 🇬🇧 🇪🇸 🇫🇷',
      inputLabel: 'Input',
      outputLabel: 'Output',
    },
    review: {
      tag: 'Google Reviews',
      h2: 'Turn negative reviews into opportunities.',
      body: 'Paste any Google review and get a professional response in seconds. Works even for 1-star attacks. Keep your reputation intact without the stress.',
      bullets: ['Responds in your language', 'Calm, professional, never aggressive', 'Ready in under 10 seconds'],
      negativeText: 'Food arrived cold and the service was extremely slow. I won\'t be coming back.',
      responseText: 'Dear Giovanni, thank you for your feedback. We\'re truly sorry to hear about your experience and we\'re committed to improving. We\'d love to welcome you back and make it right.',
      timestamp: '2 days ago',
      generatedIn: '✓ Professional response generated in 8 seconds',
    },
    faq: {
      tag: 'FAQ',
      h2: 'Frequently asked questions',
      items: [
        { q: 'Do I need a credit card for the trial?', a: 'Yes, a credit card is required. Cancel before 7 days and you won\'t be charged anything. No surprises.' },
        { q: 'What happens after the trial?', a: 'After 7 days the subscription automatically switches to €9.99/month. Cancel anytime from your dashboard, no penalties.' },
        { q: 'How is PostMio™ different from ChatGPT?', a: 'PostMio™ is purpose-built for small business social posts. No prompting, no setup. Open, type, copy. Done.' },
        { q: 'Does it work for my type of business?', a: 'Yes. PostMio™ supports 6 categories: Gastronomy, Beauty & Wellness, Graphic Design & Creative, Coaching & Consulting, Hospitality & Tourism, Retail & Shop.' },
        { q: 'What is M-LATS™?', a: 'M-LATS™ lets you write in any language and receive posts in the language saved in your profile. A feature unique to PostMio™.' },
        { q: 'Can I cancel anytime?', a: 'Yes. No contracts, no questions. Cancel from the dashboard in one click.' },
      ],
    },
    compare: {
      tag: 'Price comparison',
      h2: 'Why PostMio™?',
      sub: "Compare the real cost. It's not close.",
      smm: 'Social Media Manager',
      features: ['Unlimited posts', 'Instagram + Facebook + WhatsApp', 'Review replies', 'Category-specific AI', 'M-LATS (output in your language)', 'No limits or tokens'],
      note: '* Indicative pricing. Freelance SMM: €200–500/month estimated. Predis.ai and SocialBee: from €29/month.',
    },
    footer: {
      tagline: 'Made with ❤️ in Italy · By a small business owner - for small business owners',
      about: 'About',
      reviews: 'Reviews',
      login: 'Log in',
      signup: 'Sign up',
      legal: `© ${new Date().getFullYear()} PostMio™`,
    },
  },

  PL: {
    nav: { login: 'Zaloguj się', signup: 'Zarejestruj się', about: 'O nas', reviews: 'Opinie' },
    hero: {
      tag: 'Stworzone dla małych przedsiębiorców – przez jednego z nich',
      h1a: 'Twoje posty w mediach społecznościowych',
      h1b: 'gotowe w 10 sekund.',
      sub: 'Opisz danie dnia lub wklej recenzję Google. PostMio™ generuje 3 idealne posty na Instagram, Facebook i WhatsApp. Kopiuj i publikuj. Gotowe.',
      cta: 'Zacznij za darmo przez 7 dni →',
      fine: 'Bez opłat przez 7 dni \u00a0·\u00a0 Anuluj w każdej chwili \u00a0·\u00a0 €9,99/mies. potem',
      magicCta: 'Wypróbuj magię PostMio™ — bez karty',
    },
    problem: {
      h2a: 'Wiesz, że powinieneś częściej publikować.',
      h2b: 'Ale nigdy nie masz czasu.',
      sub: 'Kuchnia pełna, telefon dzwoni, stoliki zajęte. Media społecznościowe to ostatnia rzecz, o której myślisz — a twoi konkurenci publikują codziennie. PostMio™ rozwiązuje ten problem w 10 sekund.',
    },
    how: {
      tag: 'Jak to działa',
      h2: 'Trzy kroki. Dziesięć sekund.',
      steps: [
        { num: '1', title: 'Co dziś u Ciebie słychać?', desc: 'Napisz aktualizację, ofertę, wydarzenie — cokolwiek dzieje się dziś w Twoim miejscu.' },
        { num: '2', title: 'Wybierz ton', desc: 'Ciepły i przyjazny, profesjonalny lub zabawny. Ty wybierasz, PostMio™ pisze.' },
        { num: '3', title: 'Kopiuj i publikuj', desc: '3 posty gotowe na Instagram, Facebook i WhatsApp. Jeden klik, żeby skopiować.' },
      ],
    },
    features: {
      tag: 'Funkcje',
      h2: 'Wszystko czego potrzebujesz. Nic zbędnego.',
      list: [
        { icon: '📱', title: 'Instagram, Facebook, WhatsApp', desc: '3 posty zoptymalizowane pod każdą platformę, wygenerowane naraz.' },
        { icon: '⭐', title: 'Odpowiedzi na recenzje', desc: 'Negatywna recenzja? PostMio™ pomoże ci odpowiedzieć profesjonalnie w kilka sekund.' },
        { icon: '🎭', title: '3 różne tony', desc: 'Ciepły, profesjonalny lub zabawny. Odpowiedni ton na każdą okazję.' },
        { icon: '⚡', title: 'Gotowe w 10 sekund', desc: 'Zero czekania. Napisz, wygeneruj, skopiuj. Wróć do swoich klientów.' },
      ],
    },
    testimonials: {
      tag: 'Opinie',
      h2: 'Co mówią nasi użytkownicy',
      list: [
        { text: '"Nigdy nie miałem czasu na media społecznościowe. Z PostMio™ zajmuje mi to 30 sekund dziennie. Klienci mówią, że jesteśmy zawsze aktywni."', name: 'Marco R.', role: 'Właściciel, Trattoria da Marco — Genova' },
        { text: '"Po raz pierwszy w życiu odpowiedziałam na negatywną recenzję w profesjonalny sposób. Klient wrócił i przeprosił."', name: 'Giulia T.', role: 'Właścicielka, Bar Centrale — La Spezia' },
        { text: '"Kosztuje mniej niż kawa dziennie i oszczędza mi godziny każdego tygodnia. Nie rozumiem, dlaczego nie znalazłem tego wcześniej."', name: 'Antonio M.', role: 'Pizzaiolo, Pizzeria Bella Napoli — Napoli' },
      ],
    },
    pricing: {
      tag: 'Cennik',
      h2: 'Jedna cena. Żadnych niespodzianek.',
      sub: 'Mniej niż kawa dziennie i już nie musisz o tym myśleć.',
      period: '/mies.',
      trial: '7 dni za darmo · Potem €9,99/mies. · Anuluj w każdej chwili',
      features: [
        'Nieograniczone posty na Instagram, Facebook, WhatsApp',
        'Odpowiedzi na recenzje Google',
        '3 tony: ciepły, profesjonalny, zabawny',
        'Kopiuj jednym klikiem',
        'Natychmiastowy dostęp',
      ],
      cta: 'Zacznij za darmo przez 7 dni →',
    },
    foundingCta: {
      tag: 'Founding Members',
      spotsLine: '50 miejsc dostępnych · Wygasa 15 kwietnia 2026',
      h2: 'Wejdź teraz. Zanim będzie za późno.',
      sub: 'Pierwsze 50 subskrybentów płaci €7,99/mies. przez 6 miesięcy. Potem €9,99/mies. Użyj kodu FOUNDING2026 przy płatności.',
      codeLabel: 'Twój kod:',
      cta: 'Zostań Founding Member →',
      finePrint: 'Po 6 miesiącach automatycznie przechodzi na €9,99/mies. · Anuluj w każdej chwili',
    },
    mlats: {
      tag: 'M-LATS™',
      h2: 'Pisz w swoim języku. Publikuj w swoim.',
      body: 'Napisz swój wpis w dowolnym języku — PostMio™ generuje posty w języku zapisanym w Twoim profilu. Rozpoznaje kategorię działalności. Stworzony dla właścicieli firm z zagranicy, którzy codziennie komunikują się w drugim języku.',
      callout: 'Używane przez właścicieli firm z zagranicy w 🇮🇹 🇵🇱 🇩🇪 🇬🇧 🇪🇸 🇫🇷',
      inputLabel: 'Wejście',
      outputLabel: 'Wyjście',
    },
    review: {
      tag: 'Recenzje Google',
      h2: 'Zamień negatywne opinie w szanse.',
      body: 'Wklej dowolną recenzję Google i otrzymaj profesjonalną odpowiedź w kilka sekund. Działa nawet dla ataków 1-gwiazdkowych. Chroń swoją reputację bez stresu.',
      bullets: ['Odpowiada w Twoim języku', 'Spokojna, profesjonalna, nigdy agresywna', 'Gotowa w mniej niż 10 sekund'],
      negativeText: 'Jedzenie przyszło zimne, a obsługa była bardzo powolna. Nie wrócę tu więcej.',
      responseText: 'Drogi Giovanni, dziękujemy za opinię. Bardzo przepraszamy za to doświadczenie i zobowiązujemy się do poprawy. Serdecznie zapraszamy do ponownej wizyty — chcemy to naprawić.',
      timestamp: '2 dni temu',
      generatedIn: '✓ Profesjonalna odpowiedź wygenerowana w 8 sekund',
    },
    faq: {
      tag: 'FAQ',
      h2: 'Najczęściej zadawane pytania',
      items: [
        { q: 'Czy do okresu próbnego potrzebna jest karta kredytowa?', a: 'Tak, karta kredytowa jest wymagana. Anuluj przed upływem 7 dni — żadnych opłat. Żadnych niespodzianek.' },
        { q: 'Co się dzieje po zakończeniu okresu próbnego?', a: 'Po 7 dniach subskrypcja automatycznie przechodzi na €9,99/mies. Możesz anulować w dowolnym momencie z poziomu panelu, bez kar.' },
        { q: 'Czym PostMio™ różni się od ChatGPT?', a: 'PostMio™ jest zbudowany specjalnie do tworzenia postów w mediach społecznościowych dla małych firm. Żadnych promptów, żadnej konfiguracji. Otwórz, napisz, skopiuj. Gotowe.' },
        { q: 'Czy działa dla mojego rodzaju działalności?', a: 'Tak. PostMio™ obsługuje 6 kategorii: Gastronomia, Uroda i Wellness, Grafika i Kreatywność, Coaching i Doradztwo, Hotelarstwo i Turystyka, Handel detaliczny.' },
        { q: 'Czym jest M-LATS™?', a: 'M-LATS™ pozwala pisać w dowolnym języku i otrzymywać posty w języku zapisanym w profilu. Unikalna funkcja PostMio™.' },
        { q: 'Czy mogę anulować w dowolnym momencie?', a: 'Tak. Żadnych umów, żadnych pytań. Anuluj z panelu jednym kliknięciem.' },
      ],
    },
    compare: {
      tag: 'Porównanie cen',
      h2: 'Dlaczego PostMio™?',
      sub: 'Porównaj prawdziwy koszt. To nie jest nawet blisko.',
      smm: 'Social Media Manager',
      features: ['Nieograniczone posty', 'Instagram + Facebook + WhatsApp', 'Odpowiedzi na recenzje', 'AI specyficzne dla kategorii', 'M-LATS (wynik w Twoim języku)', 'Bez limitów i tokenów'],
      note: '* Ceny orientacyjne. Freelancer SMM: szacunkowo €200–500/mies. Predis.ai i SocialBee: od €29/mies.',
    },
    footer: {
      tagline: 'Zrobione z ❤️ we Włoszech · Od małego przedsiębiorcy - dla małych przedsiębiorców',
      about: 'O nas',
      reviews: 'Opinie',
      login: 'Zaloguj się',
      signup: 'Zarejestruj się',
      legal: `© ${new Date().getFullYear()} PostMio™`,
    },
  },

  ES: {
    nav: { login: 'Iniciar sesión', signup: 'Registrarse', about: 'Acerca de', reviews: 'Reseñas' },
    hero: {
      tag: 'Creado para pequeños empresarios – por uno de ellos',
      h1a: 'Tus publicaciones en redes sociales,',
      h1b: 'listas en 10 segundos.',
      sub: 'Describe el plato del día o pega una reseña de Google. PostMio™ genera 3 publicaciones perfectas para Instagram, Facebook y WhatsApp. Copia y publica. Listo.',
      cta: 'Empieza gratis durante 7 días →',
      fine: 'Sin cargo durante 7 días \u00a0·\u00a0 Cancela cuando quieras \u00a0·\u00a0 €9,99/mes después',
      magicCta: 'Prueba la magia de PostMio™ — sin tarjeta',
    },
    problem: {
      h2a: 'Sabes que deberías publicar más.',
      h2b: 'Pero nunca tienes tiempo.',
      sub: 'La cocina está llena, el teléfono suena, las mesas están ocupadas. Las redes sociales son lo último en lo que piensas — pero tus competidores publican cada día. PostMio™ resuelve eso en 10 segundos.',
    },
    how: {
      tag: 'Cómo funciona',
      h2: 'Tres pasos. Diez segundos.',
      steps: [
        { num: '1', title: '¿Qué pasa hoy?', desc: 'Escribe una actualización, una oferta, un evento — lo que sea que esté pasando en tu local hoy.' },
        { num: '2', title: 'Elige un tono', desc: 'Cálido y amigable, profesional o divertido. Tú eliges, PostMio™ escribe.' },
        { num: '3', title: 'Copia y publica', desc: '3 publicaciones listas para Instagram, Facebook y WhatsApp. Un clic para copiar.' },
      ],
    },
    features: {
      tag: 'Características',
      h2: 'Todo lo que necesitas. Nada más.',
      list: [
        { icon: '📱', title: 'Instagram, Facebook, WhatsApp', desc: '3 publicaciones optimizadas para cada plataforma, generadas juntas de una vez.' },
        { icon: '⭐', title: 'Respuestas a reseñas', desc: '¿Reseña negativa? PostMio™ te ayuda a responder profesionalmente en segundos.' },
        { icon: '🎭', title: '3 tonos diferentes', desc: 'Cálido, profesional o divertido. El tono adecuado para cada ocasión.' },
        { icon: '⚡', title: 'Listo en 10 segundos', desc: 'Cero espera. Escribe, genera, copia. Vuelve con tus clientes.' },
      ],
    },
    testimonials: {
      tag: 'Reseñas',
      h2: 'Lo que dicen nuestros usuarios',
      list: [
        { text: '"Nunca tuve tiempo para las redes sociales. Con PostMio™ me lleva 30 segundos al día. Los clientes dicen que siempre estamos activos."', name: 'Marco R.', role: 'Propietario, Trattoria da Marco — Génova' },
        { text: '"Por primera vez en mi vida respondí a una reseña negativa de forma profesional. El cliente volvió y se disculpó."', name: 'Giulia T.', role: 'Propietaria, Bar Centrale — La Spezia' },
        { text: '"Cuesta menos que un café al día y me ahorra horas cada semana. No puedo creer que no lo encontré antes."', name: 'Antonio M.', role: 'Pizzaiolo, Pizzeria Bella Napoli — Nápoles' },
      ],
    },
    pricing: {
      tag: 'Precio',
      h2: 'Un precio. Sin sorpresas.',
      sub: 'Menos que un café al día, sin más preocupaciones.',
      period: '/mes',
      trial: '7 días gratis · Luego €9,99/mes · Cancela cuando quieras',
      features: [
        'Publicaciones ilimitadas para Instagram, Facebook, WhatsApp',
        'Respuestas a reseñas de Google',
        '3 tonos: cálido, profesional, divertido',
        'Copia con un clic',
        'Acceso inmediato',
      ],
      cta: 'Empieza gratis durante 7 días →',
    },
    foundingCta: {
      tag: 'Founding Members',
      spotsLine: '50 plazas disponibles · Expira el 15 de abril de 2026',
      h2: 'Únete ahora. Antes de que sea tarde.',
      sub: 'Los primeros 50 suscriptores pagan €7,99/mes durante 6 meses. Luego €9,99/mes. Usa el código FOUNDING2026 al pagar.',
      codeLabel: 'Tu código:',
      cta: 'Conviértete en Founding Member →',
      finePrint: 'Tras 6 meses cambia automáticamente a €9,99/mes · Cancela cuando quieras',
    },
    mlats: {
      tag: 'M-LATS™',
      h2: 'Escribe en tu idioma. Publica en el tuyo.',
      body: 'Escribe tu actualización en cualquier idioma — PostMio™ genera publicaciones en el idioma guardado en tu perfil. Específico por categoría. Diseñado para dueños de negocios inmigrantes que gestionan su comunicación en un segundo idioma cada día.',
      callout: 'Usado por dueños de negocios inmigrantes en 🇮🇹 🇵🇱 🇩🇪 🇬🇧 🇪🇸 🇫🇷',
      inputLabel: 'Entrada',
      outputLabel: 'Salida',
    },
    review: {
      tag: 'Reseñas de Google',
      h2: 'Convierte las reseñas negativas en oportunidades.',
      body: 'Pega cualquier reseña de Google y obtén una respuesta profesional en segundos. Funciona incluso para ataques de 1 estrella. Mantén tu reputación intacta sin estrés.',
      bullets: ['Responde en tu idioma', 'Tranquila, profesional, nunca agresiva', 'Lista en menos de 10 segundos'],
      negativeText: 'La comida llegó fría y el servicio fue muy lento. No volveré.',
      responseText: 'Estimado Giovanni, gracias por su opinión. Lamentamos mucho su experiencia y nos comprometemos a mejorar. Le invitamos a darnos una segunda oportunidad para demostrarle lo que podemos hacer.',
      timestamp: 'hace 2 días',
      generatedIn: '✓ Respuesta profesional generada en 8 segundos',
    },
    faq: {
      tag: 'FAQ',
      h2: 'Preguntas frecuentes',
      items: [
        { q: '¿Necesito una tarjeta de crédito para la prueba?', a: 'Sí, es necesaria. Cancela antes de 7 días y no se realizará ningún cargo. Sin sorpresas.' },
        { q: '¿Qué pasa después de la prueba?', a: 'Tras 7 días la suscripción cambia automáticamente a €9,99/mes. Puedes cancelar en cualquier momento desde el panel, sin penalizaciones.' },
        { q: '¿En qué se diferencia PostMio™ de ChatGPT?', a: 'PostMio™ está diseñado específicamente para publicaciones en redes sociales de pequeñas empresas. Sin prompts, sin configuración. Abre, escribe, copia. Listo.' },
        { q: '¿Funciona para mi tipo de negocio?', a: 'Sí. PostMio™ admite 6 categorías: Gastronomía, Belleza y Bienestar, Diseño Gráfico y Creatividad, Coaching y Consultoría, Hospitalidad y Turismo, Retail y Tienda.' },
        { q: '¿Qué es M-LATS™?', a: 'M-LATS™ te permite escribir en cualquier idioma y recibir publicaciones en el idioma guardado en tu perfil. Una función exclusiva de PostMio™.' },
        { q: '¿Puedo cancelar en cualquier momento?', a: 'Sí. Sin contratos, sin preguntas. Cancela desde el panel con un solo clic.' },
      ],
    },
    compare: {
      tag: 'Comparación de precios',
      h2: '¿Por qué PostMio™?',
      sub: 'Compara el coste real. No hay comparación.',
      smm: 'Gestor de redes sociales',
      features: ['Publicaciones ilimitadas', 'Instagram + Facebook + WhatsApp', 'Respuestas a reseñas', 'IA específica por categoría', 'M-LATS (resultado en tu idioma)', 'Sin límites ni tokens'],
      note: '* Precios orientativos. SMM freelance: €200–500/mes estimado. Predis.ai y SocialBee: desde €29/mes.',
    },
    footer: {
      tagline: 'Hecho con ❤️ en Italia · Por un pequeño empresario - para pequeños empresarios',
      about: 'Acerca de',
      reviews: 'Reseñas',
      login: 'Iniciar sesión',
      signup: 'Registrarse',
      legal: `© ${new Date().getFullYear()} PostMio™`,
    },
  },

  DE: {
    nav: { login: 'Anmelden', signup: 'Registrieren', about: 'Über uns', reviews: 'Bewertungen' },
    hero: {
      tag: 'Gebaut für Kleinunternehmer – von einem von ihnen',
      h1a: 'Ihre Social-Media-Beiträge,',
      h1b: 'fertig in 10 Sekunden.',
      sub: 'Beschreiben Sie das Tagesgericht oder fügen Sie eine Google-Bewertung ein. PostMio™ generiert 3 perfekte Beiträge für Instagram, Facebook und WhatsApp. Kopieren und veröffentlichen. Fertig.',
      cta: 'Kostenlos 7 Tage starten →',
      fine: 'Keine Gebühr für 7 Tage \u00a0·\u00a0 Jederzeit kündbar \u00a0·\u00a0 €9,99/Monat danach',
      magicCta: 'Erlebe die PostMio™-Magie — keine Karte nötig',
    },
    problem: {
      h2a: 'Sie wissen, dass Sie mehr posten sollten.',
      h2b: 'Aber Sie haben nie die Zeit.',
      sub: 'Die Küche ist voll, das Telefon klingelt, die Tische sind belegt. Social Media ist das Letzte, woran Sie denken — aber Ihre Konkurrenten posten jeden Tag. PostMio™ löst das in 10 Sekunden.',
    },
    how: {
      tag: 'Wie es funktioniert',
      h2: 'Drei Schritte. Zehn Sekunden.',
      steps: [
        { num: '1', title: 'Was passiert heute bei Ihnen?', desc: 'Schreiben Sie ein Update, ein Angebot, ein Event — alles, was heute in Ihrem Lokal passiert.' },
        { num: '2', title: 'Wählen Sie einen Ton', desc: 'Warm und freundlich, professionell oder lustig. Sie wählen, PostMio™ schreibt.' },
        { num: '3', title: 'Kopieren und veröffentlichen', desc: '3 Beiträge fertig für Instagram, Facebook und WhatsApp. Ein Klick zum Kopieren.' },
      ],
    },
    features: {
      tag: 'Funktionen',
      h2: 'Alles was Sie brauchen. Nichts mehr.',
      list: [
        { icon: '📱', title: 'Instagram, Facebook, WhatsApp', desc: '3 plattformoptimierte Beiträge auf einmal generiert.' },
        { icon: '⭐', title: 'Bewertungsantworten', desc: 'Negative Bewertung? PostMio™ hilft Ihnen, in Sekunden professionell zu antworten.' },
        { icon: '🎭', title: '3 verschiedene Töne', desc: 'Warm, professionell oder lustig. Der richtige Ton für jeden Anlass.' },
        { icon: '⚡', title: 'Fertig in 10 Sekunden', desc: 'Null Wartezeit. Schreiben, generieren, kopieren. Zurück zu Ihren Kunden.' },
      ],
    },
    testimonials: {
      tag: 'Bewertungen',
      h2: 'Was unsere Nutzer sagen',
      list: [
        { text: '"Ich hatte nie Zeit für Social Media. Mit PostMio™ dauert es 30 Sekunden am Tag. Kunden sagen immer, wie aktiv wir sind."', name: 'Marco R.', role: 'Inhaber, Trattoria da Marco — Genua' },
        { text: '"Zum ersten Mal in meinem Leben habe ich auf eine negative Bewertung professionell geantwortet. Der Kunde kam zurück und entschuldigte sich."', name: 'Giulia T.', role: 'Inhaberin, Bar Centrale — La Spezia' },
        { text: '"Es kostet weniger als ein Kaffee am Tag und spart mir jede Woche Stunden. Ich kann nicht glauben, dass ich es nicht früher gefunden habe."', name: 'Antonio M.', role: 'Pizzabäcker, Pizzeria Bella Napoli — Neapel' },
      ],
    },
    pricing: {
      tag: 'Preise',
      h2: 'Ein Preis. Keine Überraschungen.',
      sub: 'Weniger als ein Kaffee am Tag, ohne weiteres Nachdenken.',
      period: '/Monat',
      trial: '7 Tage kostenlos · Dann €9,99/Monat · Jederzeit kündbar',
      features: [
        'Unbegrenzte Beiträge für Instagram, Facebook, WhatsApp',
        'Google-Bewertungsantworten',
        '3 Töne: warm, professionell, lustig',
        'Mit einem Klick kopieren',
        'Sofortiger Zugang',
      ],
      cta: 'Kostenlos 7 Tage starten →',
    },
    foundingCta: {
      tag: 'Founding Members',
      spotsLine: '50 Plätze verfügbar · Läuft ab am 15. April 2026',
      h2: 'Jetzt einsteigen. Bevor es zu spät ist.',
      sub: 'Die ersten 50 Abonnenten zahlen €7,99/Monat für 6 Monate. Dann €9,99/Monat. Code FOUNDING2026 bei der Zahlung eingeben.',
      codeLabel: 'Dein Code:',
      cta: 'Founding Member werden →',
      finePrint: 'Nach 6 Monaten automatisch €9,99/Monat · Jederzeit kündbar',
    },
    mlats: {
      tag: 'M-LATS™',
      h2: 'Schreiben Sie in Ihrer Sprache. Veröffentlichen Sie in Ihrer.',
      body: 'Schreiben Sie Ihre Aktualisierung in einer beliebigen Sprache — PostMio™ generiert Beiträge in der in Ihrem Profil gespeicherten Sprache. Kategoriespezifisch. Entwickelt für Unternehmer, die ihre Kommunikation täglich in einer zweiten Sprache managen.',
      callout: 'Genutzt von Unternehmen mit Migrationshintergrund in 🇮🇹 🇵🇱 🇩🇪 🇬🇧 🇪🇸 🇫🇷',
      inputLabel: 'Eingabe',
      outputLabel: 'Ausgabe',
    },
    review: {
      tag: 'Google-Bewertungen',
      h2: 'Verwandeln Sie negative Bewertungen in Chancen.',
      body: 'Fügen Sie eine Google-Bewertung ein und erhalten Sie in Sekunden eine professionelle Antwort. Funktioniert auch bei 1-Stern-Angriffen. Schützen Sie Ihren Ruf ohne Stress.',
      bullets: ['Antwortet in Ihrer Sprache', 'Ruhig, professionell, nie aggressiv', 'Fertig in unter 10 Sekunden'],
      negativeText: 'Das Essen kam kalt an und der Service war sehr langsam. Ich komme nicht wieder.',
      responseText: 'Lieber Giovanni, vielen Dank für Ihr Feedback. Es tut uns sehr leid, von Ihrer Erfahrung zu hören. Wir arbeiten kontinuierlich an Verbesserungen und laden Sie herzlich ein, uns eine zweite Chance zu geben.',
      timestamp: 'vor 2 Tagen',
      generatedIn: '✓ Professionelle Antwort in 8 Sekunden generiert',
    },
    faq: {
      tag: 'FAQ',
      h2: 'Häufig gestellte Fragen',
      items: [
        { q: 'Brauche ich eine Kreditkarte für die Testphase?', a: 'Ja, eine Kreditkarte ist erforderlich. Kündigen Sie vor 7 Tagen — keine Kosten. Keine Überraschungen.' },
        { q: 'Was passiert nach der Testphase?', a: 'Nach 7 Tagen wechselt das Abonnement automatisch auf €9,99/Monat. Sie können jederzeit über das Dashboard kündigen, ohne Strafen.' },
        { q: 'Was unterscheidet PostMio™ von ChatGPT?', a: 'PostMio™ ist speziell für Social-Media-Beiträge kleiner Unternehmen entwickelt. Kein Prompting, keine Einrichtung. Öffnen, schreiben, kopieren. Fertig.' },
        { q: 'Funktioniert es für meinen Unternehmenstyp?', a: 'Ja. PostMio™ unterstützt 6 Kategorien: Gastronomie, Beauty & Wellness, Grafikdesign & Kreatives, Coaching & Beratung, Gastgewerbe & Tourismus, Einzelhandel & Shop.' },
        { q: 'Was ist M-LATS™?', a: 'M-LATS™ ermöglicht es Ihnen, in einer beliebigen Sprache zu schreiben und Beiträge in der in Ihrem Profil gespeicherten Sprache zu erhalten. Einzigartig bei PostMio™.' },
        { q: 'Kann ich jederzeit kündigen?', a: 'Ja. Keine Verträge, keine Fragen. Kündigen Sie mit einem Klick über das Dashboard.' },
      ],
    },
    compare: {
      tag: 'Preisvergleich',
      h2: 'Warum PostMio™?',
      sub: 'Vergleichen Sie die echten Kosten. Es ist kein Vergleich.',
      smm: 'Social-Media-Manager',
      features: ['Unbegrenzte Beiträge', 'Instagram + Facebook + WhatsApp', 'Bewertungsantworten', 'Kategoriespezifische KI', 'M-LATS (Ausgabe in Ihrer Sprache)', 'Keine Limits oder Tokens'],
      note: '* Richtpreise. Freelance-SMM: geschätzt €200–500/Monat. Predis.ai und SocialBee: ab €29/Monat.',
    },
    footer: {
      tagline: 'Mit ❤️ in Italien gemacht · Von einem Kleinunternehmer - für Kleinunternehmer',
      about: 'Über uns',
      reviews: 'Bewertungen',
      login: 'Anmelden',
      signup: 'Registrieren',
      legal: `© ${new Date().getFullYear()} PostMio™`,
    },
  },

  FR: {
    nav: { login: 'Se connecter', signup: 'S\'inscrire', about: 'À propos', reviews: 'Avis' },
    hero: {
      tag: 'Créé pour les petits entrepreneurs – par l\'un d\'eux',
      h1a: 'Vos publications sur les réseaux sociaux,',
      h1b: 'prêtes en 10 secondes.',
      sub: 'Décrivez le plat du jour ou collez un avis Google. PostMio™ génère 3 publications parfaites pour Instagram, Facebook et WhatsApp. Copiez et publiez. C\'est fait.',
      cta: 'Commencer gratuitement pendant 7 jours →',
      fine: 'Sans frais pendant 7 jours \u00a0·\u00a0 Résiliez quand vous voulez \u00a0·\u00a0 €9,99/mois après',
      magicCta: 'Essayez la magie de PostMio™ — sans carte requise',
    },
    problem: {
      h2a: 'Vous savez que vous devriez publier plus.',
      h2b: 'Mais vous n\'avez jamais le temps.',
      sub: 'La cuisine est pleine, le téléphone sonne, les tables sont occupées. Les réseaux sociaux sont la dernière chose à laquelle vous pensez — pourtant vos concurrents publient chaque jour. PostMio™ résout cela en 10 secondes.',
    },
    how: {
      tag: 'Comment ça marche',
      h2: 'Trois étapes. Dix secondes.',
      steps: [
        { num: '1', title: 'Que se passe-t-il aujourd\'hui ?', desc: 'Écrivez une mise à jour, une offre, un événement — tout ce qui se passe dans votre établissement aujourd\'hui.' },
        { num: '2', title: 'Choisissez un ton', desc: 'Chaleureux et amical, professionnel ou fun. Vous choisissez, PostMio™ écrit.' },
        { num: '3', title: 'Copiez et publiez', desc: '3 publications prêtes pour Instagram, Facebook et WhatsApp. Un clic pour copier.' },
      ],
    },
    features: {
      tag: 'Fonctionnalités',
      h2: 'Tout ce dont vous avez besoin. Rien de plus.',
      list: [
        { icon: '📱', title: 'Instagram, Facebook, WhatsApp', desc: '3 publications optimisées pour chaque plateforme, générées ensemble en une fois.' },
        { icon: '⭐', title: 'Réponses aux avis', desc: 'Avis négatif ? PostMio™ vous aide à répondre professionnellement en quelques secondes.' },
        { icon: '🎭', title: '3 tons différents', desc: 'Chaleureux, professionnel ou fun. Le bon ton pour chaque occasion.' },
        { icon: '⚡', title: 'Prêt en 10 secondes', desc: 'Zéro attente. Écrivez, générez, copiez. Revenez à vos clients.' },
      ],
    },
    testimonials: {
      tag: 'Avis',
      h2: 'Ce que disent nos utilisateurs',
      list: [
        { text: '"Je n\'avais jamais le temps pour les réseaux sociaux. Avec PostMio™ ça me prend 30 secondes par jour. Les clients disent toujours à quel point nous sommes actifs."', name: 'Marco R.', role: 'Propriétaire, Trattoria da Marco — Gênes' },
        { text: '"Pour la première fois de ma vie j\'ai répondu à un avis négatif de façon professionnelle. Le client est revenu et s\'est excusé."', name: 'Giulia T.', role: 'Propriétaire, Bar Centrale — La Spezia' },
        { text: '"Ça coûte moins qu\'un café par jour et me fait gagner des heures chaque semaine. Je n\'arrive pas à croire que je ne l\'ai pas trouvé plus tôt."', name: 'Antonio M.', role: 'Pizzaiolo, Pizzeria Bella Napoli — Naples' },
      ],
    },
    pricing: {
      tag: 'Tarifs',
      h2: 'Un prix. Pas de surprises.',
      sub: 'Moins qu\'un café par jour, sans plus y penser.',
      period: '/mois',
      trial: '7 jours gratuits · Puis €9,99/mois · Résiliez quand vous voulez',
      features: [
        'Publications illimitées pour Instagram, Facebook, WhatsApp',
        'Réponses aux avis Google',
        '3 tons : chaleureux, professionnel, fun',
        'Copier en un clic',
        'Accès immédiat',
      ],
      cta: 'Commencer gratuitement pendant 7 jours →',
    },
    foundingCta: {
      tag: 'Founding Members',
      spotsLine: '50 places disponibles · Expire le 15 avril 2026',
      h2: "Rejoignez maintenant. Avant qu'il soit trop tard.",
      sub: 'Les 50 premiers abonnés paient €7,99/mois pendant 6 mois. Puis €9,99/mois. Utilisez le code FOUNDING2026 au paiement.',
      codeLabel: 'Votre code:',
      cta: 'Devenir Founding Member →',
      finePrint: 'Après 6 mois bascule automatiquement à €9,99/mois · Résiliez quand vous voulez',
    },
    mlats: {
      tag: 'M-LATS™',
      h2: 'Écrivez dans votre langue. Publiez dans la vôtre.',
      body: "Rédigez votre message dans n'importe quelle langue — PostMio™ génère les publications dans la langue enregistrée dans votre profil. Spécifique à la catégorie. Conçu pour les entrepreneurs immigrants qui gèrent leur communication dans une deuxième langue chaque jour.",
      callout: 'Utilisé par des entrepreneurs immigrants en 🇮🇹 🇵🇱 🇩🇪 🇬🇧 🇪🇸 🇫🇷',
      inputLabel: 'Entrée',
      outputLabel: 'Sortie',
    },
    review: {
      tag: 'Avis Google',
      h2: 'Transformez les avis négatifs en opportunités.',
      body: "Collez n'importe quel avis Google et obtenez une réponse professionnelle en quelques secondes. Fonctionne même pour les attaques à 1 étoile. Protégez votre réputation sans stress.",
      bullets: ['Répond dans votre langue', 'Calme, professionnel, jamais agressif', 'Prête en moins de 10 secondes'],
      negativeText: "La nourriture est arrivée froide et le service était très lent. Je ne reviendrai pas.",
      responseText: "Cher Giovanni, nous vous remercions pour votre retour. Nous sommes sincèrement désolés pour votre expérience et nous nous engageons à nous améliorer. Nous vous invitons à nous donner une deuxième chance.",
      timestamp: 'il y a 2 jours',
      generatedIn: '✓ Réponse professionnelle générée en 8 secondes',
    },
    faq: {
      tag: 'FAQ',
      h2: 'Questions fréquentes',
      items: [
        { q: "Ai-je besoin d'une carte bancaire pour l'essai ?", a: "Oui, une carte bancaire est requise. Annulez avant 7 jours — aucun prélèvement. Pas de surprises." },
        { q: "Que se passe-t-il après l'essai ?", a: "Après 7 jours l'abonnement bascule automatiquement à €9,99/mois. Résiliez à tout moment depuis le tableau de bord, sans pénalités." },
        { q: "En quoi PostMio™ est-il différent de ChatGPT ?", a: "PostMio™ est conçu spécifiquement pour les publications sur les réseaux sociaux des petites entreprises. Pas de prompts, pas de configuration. Ouvrez, écrivez, copiez. C'est tout." },
        { q: "Fonctionne-t-il pour mon type d'activité ?", a: "Oui. PostMio™ prend en charge 6 catégories : Gastronomie, Beauté & Bien-être, Graphisme & Créatif, Coaching & Conseil, Hôtellerie & Tourisme, Commerce & Boutique." },
        { q: "Qu'est-ce que M-LATS™ ?", a: "M-LATS™ vous permet d'écrire dans n'importe quelle langue et de recevoir des publications dans la langue enregistrée dans votre profil. Unique à PostMio™." },
        { q: "Puis-je annuler à tout moment ?", a: "Oui. Pas de contrat, pas de questions. Résiliez depuis le tableau de bord en un clic." },
      ],
    },
    compare: {
      tag: 'Comparaison des prix',
      h2: 'Pourquoi PostMio™ ?',
      sub: 'Comparez le coût réel. Ce n\'est pas comparable.',
      smm: 'Gestionnaire de réseaux sociaux',
      features: ['Publications illimitées', 'Instagram + Facebook + WhatsApp', 'Réponses aux avis', 'IA spécifique à la catégorie', 'M-LATS (sortie dans votre langue)', 'Sans limites ni tokens'],
      note: '* Prix indicatifs. SMM freelance : €200–500/mois estimé. Predis.ai et SocialBee : à partir de €29/mois.',
    },
    footer: {
      tagline: 'Fait avec ❤️ en Italie · Par un petit entrepreneur - pour les petits entrepreneurs',
      about: 'À propos',
      reviews: 'Avis',
      login: 'Se connecter',
      signup: 'S\'inscrire',
      legal: `© ${new Date().getFullYear()} PostMio™`,
    },
  },
}

function detectLang() {
  const lang = (navigator.language || '').toLowerCase()
  if (lang.startsWith('pl')) return 'PL'
  if (lang.startsWith('en')) return 'EN'
  if (lang.startsWith('es')) return 'ES'
  if (lang.startsWith('de')) return 'DE'
  if (lang.startsWith('fr')) return 'FR'
  return 'IT'
}

function renderTM(s) {
  if (typeof s !== 'string' || !s.includes('™')) return s
  const i = s.indexOf('™')
  return <>{s.slice(0, i)}<sup style={{fontSize:'0.5em',verticalAlign:'super',lineHeight:0}}>™</sup>{s.slice(i + 1)}</>
}

export default function Landing() {
  const t = UI_TEXT[detectLang()]
  const handleStart = () => {} // payments disabled
  const [openFaq, setOpenFaq] = useState(null)
  const { deferredPrompt, setDeferredPrompt } = usePWAInstall()

  const [displayReviews, setDisplayReviews] = useState([])
  useEffect(() => {
    async function fetchTopReviews() {
      const { data } = await supabase
        .from('reviews')
        .select('*')
        .eq('approved', true)
        .order('rating', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(3)
      const lang = detectLang()
      if (!data || data.length === 0) {
        setDisplayReviews(data || [])
        return
      }
      try {
        const res = await fetch('/api/translate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ texts: data.map(r => r.review_text), targetLang: lang })
        })
        if (!res.ok) throw new Error(`translate ${res.status}`)
        const json = await res.json()
        const translated = json.translated || data.map(r => r.review_text)
        setDisplayReviews(data.map((r, i) => ({ ...r, review_text: translated[i] ?? r.review_text })))
      } catch {
        setDisplayReviews(data)
      }
    }
    fetchTopReviews()
  }, [])

  return (
    <div style={{ background: G.bg, fontFamily: "'DM Sans', sans-serif", color: G.cream }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:wght@600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${G.bg}; }
        .land-cta-primary {
          background: ${G.gold}; color: #0A0A0A;
          padding: 15px 36px; border-radius: 10px;
          font-size: 16px; font-weight: 700; border: none; cursor: pointer;
          font-family: 'DM Sans', sans-serif; letter-spacing: 0.2px;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          display: inline-flex; align-items: center; gap: 8px;
        }
        .land-cta-primary:hover {
          background: ${G.goldLt};
          transform: translateY(-1px);
          box-shadow: 0 6px 24px rgba(201,146,42,0.3);
        }
        .land-cta-secondary {
          background: transparent; color: ${G.gold};
          padding: 13px 28px; border-radius: 10px;
          font-size: 15px; font-weight: 600; border: 1.5px solid ${G.gold};
          cursor: pointer; font-family: 'DM Sans', sans-serif;
          transition: all 0.2s;
        }
        .land-cta-secondary:hover { background: ${G.goldGlow}; color: ${G.goldLt}; border-color: ${G.goldLt}; }
        .land-card {
          background: ${G.card}; border: 1px solid ${G.border};
          border-radius: 14px; padding: 28px 24px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .land-card:hover {
          border-color: ${G.borderHi};
          box-shadow: 0 0 24px rgba(201,146,42,0.08);
        }
        .land-step-num {
          width: 40px; height: 40px;
          background: ${G.goldGlow}; border: 1px solid ${G.gold};
          border-radius: 10px; display: flex; align-items: center;
          justify-content: center; font-family: 'Lora', serif;
          font-size: 18px; font-weight: 700; color: ${G.gold};
          margin-bottom: 16px; flex-shrink: 0;
        }
        .land-check { color: ${G.gold}; margin-right: 10px; }
        .land-section-tag {
          display: inline-block;
          background: ${G.goldGlow}; border: 1px solid ${G.gold};
          border-radius: 20px; padding: 4px 14px;
          font-size: 11px; font-weight: 600; color: ${G.gold};
          letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 24px;
        }
        .land-divider { border: none; border-top: 1px solid ${G.border}; margin: 0; }
        @media (max-width: 639px) {
          .land-hero-h1 { line-height: 1.07 !important; }
        }
        @media (max-width: 640px) {
          .land-compare-table th,
          .land-compare-table td {
            padding: 8px 6px !important;
            font-size: 11px !important;
          }
          .land-compare-table th:first-child,
          .land-compare-table td:first-child {
            min-width: 90px;
            white-space: normal !important;
          }
        }
      `}</style>

      {/* ── NAVBAR ─────────────────────────────────────────── */}
      <nav className="site-nav" style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(10,10,10,0.92)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${G.border}`,
        padding: '0 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: 60,
      }}>
        <Link to="/" style={{ textDecoration: 'none', lineHeight: 0 }}>
          <PostMioLogo size="md" variant="dark" />
        </Link>
        <div className="site-nav-links" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link to="/about" style={{ fontSize: 14, color: G.smoke, textDecoration: 'none', fontWeight: 500, transition: 'color 0.2s' }}
            onMouseEnter={e => e.target.style.color = G.cream}
            onMouseLeave={e => e.target.style.color = G.smoke}
          >
            {t.nav.about}
          </Link>
          <Link to="/reviews" style={{ fontSize: 14, color: G.smoke, textDecoration: 'none', fontWeight: 500, transition: 'color 0.2s' }}
            onMouseEnter={e => e.target.style.color = G.cream}
            onMouseLeave={e => e.target.style.color = G.smoke}
          >
            {t.nav.reviews}
          </Link>
          <Link to="/login" style={{ fontSize: 14, color: G.smoke, textDecoration: 'none', fontWeight: 500, transition: 'color 0.2s' }}
            onMouseEnter={e => e.target.style.color = G.cream}
            onMouseLeave={e => e.target.style.color = G.smoke}
          >
            {t.nav.login}
          </Link>
          <button className="land-cta-primary" onClick={handleStart} disabled style={{ padding: '8px 18px', fontSize: 13, cursor: 'default', opacity: 0.6, pointerEvents: 'none' }}>
            🚀 Coming Soon
          </button>
        </div>
      </nav>

      {/* ── PWA INSTALL BANNER ─────────────────────────────── */}
      {deferredPrompt && (
        <div style={{ background: '#141414', borderBottom: '1px solid #2C2C2C', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap', textAlign: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#F0EDE8', fontFamily: "'DM Sans', sans-serif" }}>
            Download the PostMio™ App now.
          </span>
          <button
            className="land-cta-primary"
            style={{ padding: '8px 20px', fontSize: 13 }}
            onClick={async () => { deferredPrompt.prompt(); await deferredPrompt.userChoice; }}
          >
            Install App
          </button>
        </div>
      )}

      {/* ── HERO ───────────────────────────────────────────── */}
      <section style={{ maxWidth: 720, margin: '0 auto', padding: '28px 24px 80px', textAlign: 'center' }}>
        <span className="land-section-tag" style={{ textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: 13, fontWeight: 600 }}>
          {t.hero.tag}
        </span>
        <h1 className="land-hero-h1" style={{
          fontFamily: "'Lora', serif",
          fontSize: 'clamp(34px, 6vw, 58px)',
          fontWeight: 700, lineHeight: 1.13,
          color: G.cream, marginBottom: 24,
        }}>
          {t.hero.h1a}<br />
          <span style={{ color: G.gold }}>{t.hero.h1b}</span>
        </h1>
        <p className="land-hero-sub" style={{ fontSize: 20, color: G.textSec, lineHeight: 1.5, maxWidth: 520, margin: '0 auto 40px' }}>
          {renderTM(t.hero.sub)}
        </p>
        <button className="land-cta-primary" onClick={handleStart} disabled style={{ padding: '16px 40px', fontSize: 17, cursor: 'default', opacity: 0.6, pointerEvents: 'none' }}>
          🚀 Coming Soon — Stay tuned for launch!
        </button>
        <p style={{ fontSize: 15, color: G.textMut, marginTop: 16, lineHeight: 1.5 }}>
          {t.hero.fine}
        </p>
        <div style={{ marginTop: 22, textAlign: 'center' }}>
          <Link
            to="/demo"
            style={{
              fontSize: 15,
              color: G.smoke,
              textDecoration: 'none',
              borderBottom: `1px solid ${G.gold}`,
              paddingBottom: 1,
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 500,
            }}
          >
            ✨ {renderTM(t.hero.magicCta)}
          </Link>
        </div>
      </section>

      <hr className="land-divider" />

      {/* ── PROBLEM ────────────────────────────────────────── */}
      <section style={{ background: G.base, padding: '72px 24px' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontFamily: "'Lora', serif",
            fontSize: 'clamp(24px, 4vw, 38px)',
            fontWeight: 700, color: G.cream,
            marginBottom: 20, lineHeight: 1.3,
          }}>
            {t.problem.h2a}<br />
            <span style={{ color: G.gold }}>{t.problem.h2b}</span>
          </h2>
          <p style={{ fontSize: 18, color: G.textSec, lineHeight: 1.5 }}>
            {renderTM(t.problem.sub)}
          </p>
        </div>
      </section>

      <hr className="land-divider" />

      {/* ── HOW IT WORKS ───────────────────────────────────── */}
      <section style={{ padding: '80px 24px', maxWidth: 960, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <span className="land-section-tag">{t.how.tag}</span>
          <h2 style={{ fontFamily: "'Lora', serif", fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 700, color: G.cream }}>
            {t.how.h2}
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>
          {t.how.steps.map(s => (
            <div key={s.num} className="land-card" style={{ position: 'relative', overflow: 'hidden' }}>
              <div style={{
                fontSize: '120px', fontFamily: "'Lora', serif", fontWeight: 700,
                color: G.gold, opacity: 0.15, lineHeight: 1,
                position: 'absolute', top: '-10px', left: '8px',
                userSelect: 'none', pointerEvents: 'none',
              }}>{s.num}</div>
              <h3 style={{ fontFamily: "'Lora', serif", fontSize: 18, fontWeight: 700, color: G.cream, marginBottom: 10, position: 'relative', zIndex: 1 }}>{s.title}</h3>
              <p style={{ fontSize: 16, color: G.textSec, lineHeight: 1.5, position: 'relative', zIndex: 1 }}>{renderTM(s.desc)}</p>
            </div>
          ))}
        </div>
      </section>

      <hr className="land-divider" />

      {/* ── FEATURES ───────────────────────────────────────── */}
      <section style={{ background: G.base, padding: '80px 24px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <span className="land-section-tag">{t.features.tag}</span>
            <h2 style={{ fontFamily: "'Lora', serif", fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 700, color: G.cream }}>
              {t.features.h2}
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
            {t.features.list.map(f => (
              <div key={f.title} className="land-card" style={{ padding: '24px 20px' }}>
                <div style={{ fontSize: 28, marginBottom: 14 }}>{f.icon}</div>
                <h3 style={{ fontFamily: "'Lora', serif", fontSize: 16, fontWeight: 700, color: G.cream, marginBottom: 8 }}>{f.title}</h3>
                <p style={{ fontSize: 15, color: G.textSec, lineHeight: 1.5 }}>{renderTM(f.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="land-divider" />

      {/* ── TESTIMONIALS ───────────────────────────────────── */}
      <section style={{ padding: '80px 24px', maxWidth: 960, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <span className="land-section-tag">{t.testimonials.tag}</span>
          <h2 style={{ fontFamily: "'Lora', serif", fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 700, color: G.cream }}>
            {t.testimonials.h2}
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
          {displayReviews.length > 0
            ? displayReviews.map(r => (
                <div key={r.id} className="land-card">
                  <div style={{ fontSize: 28, color: G.gold, marginBottom: 16, fontFamily: "'Lora', serif" }}>❝</div>
                  <div style={{ marginBottom: 12 }}>
                    {[1,2,3,4,5].map(s => (
                      <span key={s} style={{ color: s <= r.rating ? G.gold : G.smoke, fontSize: 18 }}>★</span>
                    ))}
                  </div>
                  <p style={{ fontSize: 16, color: G.textSec, lineHeight: 1.5, marginBottom: 20, fontStyle: 'italic' }}>{r.review_text}</p>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: G.cream }}>{r.name}</div>
                    {r.business_type && <div style={{ fontSize: 12, color: G.smoke, marginTop: 2 }}>{r.business_type}</div>}
                    <div style={{ fontSize: 12, color: G.smoke, marginTop: 2 }}>
                      {new Date(r.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </div>
                  </div>
                </div>
              ))
            : t.testimonials.list.map(item => (
                <div key={item.name} className="land-card">
                  <div style={{ fontSize: 28, color: G.gold, marginBottom: 16, fontFamily: "'Lora', serif" }}>❝</div>
                  <p style={{ fontSize: 16, color: G.textSec, lineHeight: 1.5, marginBottom: 20, fontStyle: 'italic' }}>{renderTM(item.text)}</p>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: G.cream }}>{item.name}</div>
                    <div style={{ fontSize: 12, color: G.smoke, marginTop: 3 }}>{item.role}</div>
                  </div>
                </div>
              ))
          }
        </div>
        {displayReviews.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <Link to="/reviews" style={{ fontSize: 14, color: G.gold, textDecoration: 'none', fontWeight: 600 }}>
              {detectLang() === 'IT' ? 'Leggi tutte le recensioni →' : detectLang() === 'PL' ? 'Przeczytaj wszystkie opinie →' : 'Read all reviews →'}
            </Link>
          </div>
        )}
      </section>

      <hr className="land-divider" />

      {/* ── COMPARISON TABLE ───────────────────────────────── */}
      <section className="land-compare-section" style={{ background: G.bg, padding: '80px 0' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <span className="land-section-tag">{t.compare.tag}</span>
            <h2 style={{ fontFamily: "'Lora', serif", fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 700, color: G.cream, marginBottom: 12 }}>
              {renderTM(t.compare.h2)}
            </h2>
            <p style={{ fontSize: 18, color: G.textSec, lineHeight: 1.5 }}>{t.compare.sub}</p>
          </div>
        </div>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 16px' }}>
          <div style={{ borderRadius: 12, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table className="land-compare-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                <thead>
                  <tr style={{ background: G.card, borderBottom: `2px solid ${G.gold}` }}>
                    <th style={{ fontFamily: "'Lora', serif", fontSize: 15, fontWeight: 700, padding: '14px 20px', textAlign: 'left', color: G.cream }}>Feature</th>
                    <th style={{ fontFamily: "'Lora', serif", fontSize: 15, fontWeight: 700, padding: '14px 20px', textAlign: 'center', color: G.gold }}>PostMio<sup style={{fontSize:'0.5em',verticalAlign:'super',lineHeight:0}}>™</sup></th>
                    <th style={{ fontFamily: "'Lora', serif", fontSize: 15, fontWeight: 700, padding: '14px 20px', textAlign: 'center', color: G.cream }}>Buffer</th>
                    <th style={{ fontFamily: "'Lora', serif", fontSize: 15, fontWeight: 700, padding: '14px 20px', textAlign: 'center', color: G.cream }}>Ocoya</th>
                    <th style={{ fontFamily: "'Lora', serif", fontSize: 15, fontWeight: 700, padding: '14px 20px', textAlign: 'center', color: G.cream }}>Predis.ai</th>
                    <th style={{ fontFamily: "'Lora', serif", fontSize: 15, fontWeight: 700, padding: '14px 20px', textAlign: 'center', color: G.cream }}>SocialBee</th>
                    <th style={{ fontFamily: "'Lora', serif", fontSize: 15, fontWeight: 700, padding: '14px 20px', textAlign: 'center', color: G.cream }}>{t.compare.smm}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    [true,  true,  true,  false, false, true ],
                    [true,  false, false, false, false, true ],
                    [true,  false, false, false, false, true ],
                    [true,  false, false, false, false, null ],
                    [true,  false, false, false, false, null ],
                    [true,  false, false, false, false, true ],
                  ].map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? G.card : G.bg }}>
                      <td style={{ padding: '12px 20px', textAlign: 'left', color: G.textSec, fontSize: 14, borderBottom: `1px solid ${G.border}` }}>
                        {t.compare.features[i]}
                      </td>
                      <td style={{ padding: '12px 20px', textAlign: 'center', fontSize: 18, borderBottom: `1px solid ${G.border}`, background: 'rgba(201,146,42,0.04)' }}>
                        <span style={{ color: G.gold, fontWeight: 700 }}>✓</span>
                      </td>
                      {row.slice(1).map((val, j) => (
                        <td key={j} style={{ padding: '12px 20px', textAlign: 'center', fontSize: 18, borderBottom: `1px solid ${G.border}` }}>
                          {val === null
                            ? <span style={{ color: G.smoke }}>—</span>
                            : val
                              ? <span style={{ color: G.gold, fontWeight: 700 }}>✓</span>
                              : <span style={{ color: G.textMut }}>✗</span>
                          }
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr style={{ background: G.card }}>
                    <td style={{ padding: '12px 20px', textAlign: 'left', fontSize: 14, borderBottom: `1px solid ${G.border}`, fontFamily: "'Lora', serif", fontWeight: 700, color: G.cream }}>
                      Price
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', borderBottom: `1px solid ${G.border}`, fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 20, color: G.gold, background: 'rgba(201,146,42,0.04)' }}>
                      €9.99/mo
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', borderBottom: `1px solid ${G.border}`, fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 16, color: G.smoke }}>
                      €6+/mo
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', borderBottom: `1px solid ${G.border}`, fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 16, color: G.smoke }}>
                      €19+/mo
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', borderBottom: `1px solid ${G.border}`, fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 16, color: G.smoke }}>
                      €29+/mo
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', borderBottom: `1px solid ${G.border}`, fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 16, color: G.smoke }}>
                      €29+/mo
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', borderBottom: `1px solid ${G.border}`, fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 16, color: G.smoke }}>
                      €200–500/mo
                    </td>
                  </tr>
                </tbody>
              </table>
          </div>
        </div>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 24px' }}>
          <p style={{ fontSize: 12, color: G.textMut, textAlign: 'center', marginTop: 16 }}>{t.compare.note}</p>
        </div>
      </section>

      <hr className="land-divider" />

      {/* ── M-LATS™ SPOTLIGHT ──────────────────────────────── */}
      <section style={{ background: G.bg, padding: '80px 24px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 48, alignItems: 'center' }}>
            {/* Left: text */}
            <div>
              <span className="land-section-tag">{renderTM(t.mlats.tag)}</span>
              <h2 style={{ fontFamily: "'Lora', serif", fontSize: 'clamp(26px, 4vw, 42px)', fontWeight: 700, color: G.cream, marginBottom: 20, lineHeight: 1.25 }}>
                {t.mlats.h2}
              </h2>
              <p style={{ fontSize: 17, color: G.textSec, lineHeight: 1.65, marginBottom: 28 }}>
                {t.mlats.body}
              </p>
              <div style={{ background: G.goldGlow, border: `1px solid ${G.gold}`, borderRadius: 10, padding: '16px 20px', fontSize: 14, color: G.cream, lineHeight: 1.5 }}>
                {t.mlats.callout}
              </div>
            </div>
            {/* Right: visual mockup */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className="land-card" style={{ maxWidth: 320, width: '100%', padding: '28px 24px' }}>
                <div style={{ marginBottom: 4, fontSize: 11, fontWeight: 600, color: G.textMut, textTransform: 'uppercase', letterSpacing: 1 }}>
                  {t.mlats.inputLabel} 🇵🇱
                </div>
                <div style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 8, padding: '12px 14px', fontSize: 14, color: G.textSec, lineHeight: 1.5, marginBottom: 16 }}>
                  Dzisiaj mamy świeżą pizzę margherita!
                </div>
                <div style={{ textAlign: 'center', fontSize: 22, color: G.gold, marginBottom: 16 }}>↓</div>
                <div style={{ marginBottom: 4, fontSize: 11, fontWeight: 600, color: G.textMut, textTransform: 'uppercase', letterSpacing: 1 }}>
                  {t.mlats.outputLabel} 🇮🇹
                </div>
                <div style={{ background: G.bg, border: `1px solid ${G.gold}`, borderRadius: 8, padding: '12px 14px', fontSize: 14, color: G.gold, lineHeight: 1.5, marginBottom: 16 }}>
                  Oggi abbiamo una fresca pizza margherita! 🍕
                </div>
                <div style={{ textAlign: 'center' }}>
                  <span style={{ display: 'inline-block', background: G.goldGlow, border: `1px solid ${G.gold}`, borderRadius: 20, padding: '3px 12px', fontSize: 11, color: G.gold, fontWeight: 600, letterSpacing: 0.5 }}>
                    {renderTM('PostMio™')} {renderTM('M-LATS™')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="land-divider" />

      {/* ── REVIEW RESPONSE SPOTLIGHT ──────────────────────── */}
      <section style={{ background: G.base, padding: '80px 24px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 48, alignItems: 'center' }}>
            {/* Left: visual mockup */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ maxWidth: 340, width: '100%' }}>
                {/* Negative review card */}
                <div style={{ background: G.card, border: `1px solid ${G.red}`, borderRadius: 14, padding: '20px', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ fontSize: 13, color: G.textMut }}>Giovanni M.</div>
                    <div style={{ fontSize: 13, color: G.textMut }}>{t.review.timestamp}</div>
                  </div>
                  <div style={{ fontSize: 15, marginBottom: 10, letterSpacing: 1 }}>
                    <span style={{ color: G.gold }}>★</span>
                    <span style={{ color: G.border }}>★★★★</span>
                  </div>
                  <p style={{ fontSize: 14, color: G.textSec, lineHeight: 1.5, margin: 0 }}>{t.review.negativeText}</p>
                </div>
                {/* Arrow */}
                <div style={{ textAlign: 'center', fontSize: 22, color: G.gold, marginBottom: 12 }}>↓</div>
                {/* PostMio response card */}
                <div style={{ background: G.card, border: `1px solid ${G.gold}`, borderRadius: 14, padding: '20px', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 12, right: 14, background: G.goldGlow, border: `1px solid ${G.gold}`, borderRadius: 20, padding: '2px 10px', fontSize: 10, color: G.gold, fontWeight: 700, letterSpacing: 0.5 }}>
                    {renderTM('PostMio™')}
                  </div>
                  <p style={{ fontSize: 14, color: G.textSec, lineHeight: 1.6, margin: '0 0 14px', paddingRight: 60 }}>{t.review.responseText}</p>
                  <div style={{ fontSize: 12, color: G.gold, fontWeight: 600 }}>{t.review.generatedIn}</div>
                </div>
              </div>
            </div>
            {/* Right: text */}
            <div>
              <span className="land-section-tag">{t.review.tag}</span>
              <h2 style={{ fontFamily: "'Lora', serif", fontSize: 'clamp(26px, 4vw, 42px)', fontWeight: 700, color: G.cream, marginBottom: 20, lineHeight: 1.25 }}>
                {t.review.h2}
              </h2>
              <p style={{ fontSize: 17, color: G.textSec, lineHeight: 1.65, marginBottom: 28 }}>
                {t.review.body}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {t.review.bullets.map((b, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 15, color: G.textSec }}>
                    <span style={{ color: G.gold, fontWeight: 700, flexShrink: 0 }}>✓</span>
                    {b}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="land-divider" />

      {/* ── FAQ ────────────────────────────────────────────── */}
      <section style={{ background: G.bg, padding: '80px 24px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <span className="land-section-tag">{t.faq.tag}</span>
            <h2 style={{ fontFamily: "'Lora', serif", fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 700, color: G.cream, lineHeight: 1.25 }}>
              {t.faq.h2}
            </h2>
          </div>
          <div>
            {t.faq.items.map((item, i) => (
              <div key={i} style={{ borderBottom: `1px solid ${G.border}` }}>
                <div
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 0', cursor: 'pointer' }}
                >
                  <span style={{ fontWeight: 500, fontSize: 16, color: G.cream, lineHeight: 1.4, paddingRight: 16 }}>{renderTM(item.q)}</span>
                  <span style={{ color: G.gold, fontSize: 20, flexShrink: 0, lineHeight: 1 }}>{openFaq === i ? '×' : '+'}</span>
                </div>
                {openFaq === i && (
                  <div style={{ padding: '0 0 18px', fontSize: 15, color: G.textSec, lineHeight: 1.6 }}>
                    {renderTM(item.a)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="land-divider" />

      {/* ── PRICING ────────────────────────────────────────── */}
      <section style={{ background: G.base, padding: '80px 24px' }}>
        <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
          <span className="land-section-tag">{t.pricing.tag}</span>
          <h2 style={{ fontFamily: "'Lora', serif", fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 700, color: G.cream, marginBottom: 12 }}>
            {t.pricing.h2}
          </h2>
          <p style={{ fontSize: 18, color: G.textSec, lineHeight: 1.5, marginBottom: 40 }}>
            {t.pricing.sub}
          </p>
          <div style={{
            background: G.card, border: `2px solid ${G.gold}`,
            borderRadius: 20, padding: '40px 32px',
            boxShadow: `0 8px 48px rgba(201,146,42,0.15)`,
          }}>
            <div style={{
              display: 'inline-block', background: G.gold, color: '#0A0A0A',
              borderRadius: 20, padding: '4px 18px',
              fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
              textTransform: 'uppercase', marginBottom: 24,
            }}>PostMio<sup style={{fontSize:'0.5em',verticalAlign:'super',lineHeight:0}}>™</sup> Pro</div>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontFamily: "'Lora', serif", fontSize: 56, fontWeight: 700, color: G.cream }}>€9,99</span>
              <span style={{ fontSize: 16, color: G.smoke }}>{t.pricing.period}</span>
            </div>
            <p style={{ fontSize: 15, color: G.smoke, lineHeight: 1.5, marginBottom: 32 }}>
              {t.pricing.trial}
            </p>
            {t.pricing.features.map(item => (
              <div key={item} style={{
                fontSize: 14, color: G.textSec,
                padding: '9px 0', textAlign: 'left',
                borderBottom: `1px solid ${G.border}`,
              }}>
                <span className="land-check">✓</span>
                {item}
              </div>
            ))}
            <button className="land-cta-primary" onClick={handleStart} disabled style={{ width: '100%', marginTop: 32, padding: '15px', fontSize: 16, justifyContent: 'center', cursor: 'default', opacity: 0.6, pointerEvents: 'none' }}>
              🚀 Coming Soon — Stay tuned for launch!
            </button>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ──────────────────────────────────────── */}
      <section style={{
        background: `linear-gradient(135deg, #1a1000 0%, #0A0A0A 50%, #100800 100%)`,
        borderTop: `1px solid ${G.gold}`,
        padding: '88px 24px', textAlign: 'center',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div className="land-section-tag" style={{ color: G.gold, marginBottom: 0 }}>{t.foundingCta.tag}</div>
          <div style={{
            display: 'inline-block',
            background: 'rgba(201,146,42,0.12)',
            border: `1px solid ${G.gold}`,
            borderRadius: 20,
            padding: '6px 16px',
            fontSize: 13,
            color: G.gold,
            fontWeight: 600,
          }}>
            {t.foundingCta.spotsLine}
          </div>
        </div>
        <h2 style={{
          fontFamily: "'Lora', serif",
          fontSize: 'clamp(26px, 4vw, 44px)',
          fontWeight: 700, color: G.cream,
          marginBottom: 16, lineHeight: 1.3,
        }}>
          {t.foundingCta.h2}
        </h2>
        <p style={{ fontSize: 18, color: G.textSec, lineHeight: 1.5, maxWidth: 520, margin: '0 auto 32px' }}>
          {t.foundingCta.sub}
        </p>
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 13, color: G.textMut, marginBottom: 8 }}>{t.foundingCta.codeLabel}</div>
          <div style={{
            display: 'inline-block',
            background: '#0A0A0A',
            border: `1.5px solid ${G.gold}`,
            borderRadius: 10,
            padding: '10px 24px',
            fontFamily: 'monospace',
            fontSize: 22,
            fontWeight: 700,
            color: G.gold,
            letterSpacing: 3,
          }}>
            FOUNDING2026
          </div>
        </div>
        <div>
          <button
            className="land-cta-primary"
            onClick={() => {}}
            disabled
            style={{ padding: '17px 44px', fontSize: 17, cursor: 'default', opacity: 0.6, pointerEvents: 'none' }}
          >
            🚀 Coming Soon — Stay tuned for launch!
          </button>
          <p style={{ fontSize: 13, color: G.textMut, marginTop: 16 }}>{t.foundingCta.finePrint}</p>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────── */}
      <footer style={{ background: G.bg, borderTop: `1px solid ${G.border}`, padding: '40px 24px', textAlign: 'center' }}>
        <PostMioLogo size="md" variant="dark" />
        <p style={{ fontSize: 14, color: G.textMut, marginTop: 16, lineHeight: 1.5 }}>
          {t.footer.tagline}<br />
          <Link to="/about" style={{ color: G.textMut, textDecoration: 'none' }}>{t.footer.about}</Link>
          {' · '}
          <Link to="/reviews" style={{ color: G.textMut, textDecoration: 'none' }}>{t.footer.reviews}</Link>
          {' · '}
          <Link to="/login" style={{ color: G.textMut, textDecoration: 'none' }}>{t.footer.login}</Link>
          {' · '}
          <Link to="/signup" style={{ color: G.textMut, textDecoration: 'none' }}>{t.footer.signup}</Link>
          {' · '}
          <Link to="/privacy" style={{ color: G.textMut, textDecoration: 'none' }}>Privacy Policy</Link>
          {' · '}
          <a href="https://senzon.eu" target="_blank" rel="noopener noreferrer" style={{ color: G.textMut, textDecoration: 'none' }}>Built by SenZon™</a>
        </p>
        <p style={{ fontSize: 13, color: G.border, marginTop: 12, lineHeight: 1.5 }}>
          {renderTM(t.footer.legal)}
        </p>
      </footer>
    </div>
  )
}
